import { useQueries } from 'react-query';
import { StorefrontClient } from '@jbhi-fi/apis/src/storefront/StorefrontClient';
import { LocalStorageCache } from '@jbhi-fi/utils/src/localStorageCache/localStorageCache';

export type UseSellerUrlHandleResult = {
  data: { id: string; handle: string } | undefined;
  isLoading: boolean;
  error?: unknown;
};

export const useSellerUrlHandle = ({
  storefrontClient,
  sellerIds,
}: {
  storefrontClient: StorefrontClient;
  sellerIds: Array<string>;
}): Array<UseSellerUrlHandleResult> => {
  const storageKey = `seller-handle`;

  const setSellerHandleCache = (sellerId: string, handle: string) => {
    if (!sellerId || !handle) return;
    LocalStorageCache.set(`${storageKey}-${sellerId}`, handle);
  };

  const getCachedSellerHandle = (sellerId: string): string | undefined => {
    if (!sellerId) return;
    return LocalStorageCache.get(`${storageKey}-${sellerId}`);
  };

  const results = useQueries(
    sellerIds
      ?.map(sellerId => ({
        enabled: Boolean(sellerId),
        queryKey: ['useSellerUrlHandle', sellerId],
        queryFn: async () => {
          const cachedHandle = getCachedSellerHandle(sellerId);
          if (cachedHandle) return { id: sellerId, handle: cachedHandle };

          const metaobject = await storefrontClient.getSellerMetaobjectById(sellerId);
          const handle = metaobject?.url_handle.value ?? '';
          setSellerHandleCache(sellerId, handle);
          return { id: sellerId, handle };
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        /* Cache for up to 15 minutes. */
        staleTime: 15 * 60 * 1000,
        cacheTime: 15 * 60 * 1000,
      }))
      .filter(query => query.enabled)
  );

  return results.map(res => ({
    isLoading: res.status === 'loading',
    data: res.data,
    error: res.error,
  }));
};
